// 画像タイプ
export type ImageType = 'portrait' | 'illustration' | 'background' | 'object'

// 立ち絵構図（portraitタイプで使用）
export type PortraitComposition = 'face' | 'upper_body' | 'waist_up' | 'full_body'

// 背景タイプ
export type BackgroundType = 'scene' | 'solid_color'

// AIモデル（2025年最新版）
export type AIModel = 
  | 'novelai_v45'      // NovelAI V4.5
  | 'midjourney_v7'    // Midjourney V7
  | 'sdxl_standard'    // SDXL標準
  | 'pony_v6'          // Pony Diffusion V6 XL
  | 'animagine_v4'     // Animagine XL 4.0
  | 'illustrious'      // Illustrious XL
  | 'noobai_xl'        // NoobAI XL

// 手動入力のキャラクター情報
export interface ManualCharacterInput {
  // 基本情報
  characterName?: string
  gender?: 'male' | 'female' | 'other'
  age?: string
  ageGroup?: string       // 年齢層（子供、青年、大人等）
  
  // 外見詳細
  hair?: {
    front?: string        // 前髪（ぱっつん、流し前髪等）
    style?: string        // 髪型（ポニーテール、ボブ等）
    texture?: string      // 髪質（ストレート、ウェーブ、カール、無造作）
    color?: string        // 基本髪色（黒髪、茶髪、金髪等）
    colorEffect?: string  // 色効果（グラデーション、メッシュ）
  }
  eyes?: {
    shape?: string        // 目の形状（ツリ目、タレ目等）
    size?: string         // 目の大きさ
    color?: string        // 目の色
  }
  expression?: string     // 表情
  faceDetails?: {
    features?: string[]   // 特徴（ほくろ、傷等）
  }
  bodyType?: string
  bodyTypeDetail?: string  // カスタム体型の詳細説明
  
  // 服装
  clothing?: {
    tops?: string
    topsColor?: string      // トップスの色
    bottoms?: string
    bottomsColor?: string   // ボトムスの色
    onepiece?: string       // ワンピース系（ドレス、着物等）
    onepieceColor?: string  // ワンピースの色
    shoes?: string
    shoesColor?: string     // 靴の色
    accessories?: string[]
  }
  
  // ポーズ・構図
  pose?: {
    standing?: string     // 立ちポーズ
    gesture?: string      // ジェスチャー
    viewAngle?: string    // 視点
  }
  
  // 世界観・環境
  worldSetting?: {
    type?: string         // ジャンル（ファンタジー、SF等）
    era?: string          // 時代設定
    location?: string     // 場所
    atmosphere?: string   // 雰囲気
  }
  
  // 画風
  artStyle?: {
    period?: string       // 年代（80年代風等）
    technique?: string    // タッチ（水彩、アニメ塗り等）
    mood?: string         // 雰囲気（明るい、ダーク等）
    detailLevel?: string  // 詳細度（シンプル、超詳細等）
    customStyle?: string  // カスタム画風の詳細
  }
  
  // その他
  freeText?: string       // 自由記述テキスト
  
  // 追加情報（エルフ、亜人などの特殊設定用）
  additionalInfo?: {
    text?: string           // 自由入力テキスト
    savedCharacterId?: string  // 保存済みキャラクターID
    savedEnemyId?: string      // 保存済みエネミーID
    enemyView?: 'none' | 'front' | 'side'  // エネミー/戦闘用ビュー設定
    battleGraphicStyle?: string  // 戦闘用グラフィック絵柄プリセット
    uploadedFile?: string   // アップロードファイル内容
  }
}

// 入力インターフェース
export interface ImagePromptInput {
  // 基本設定
  imageType: ImageType
  composition?: PortraitComposition
  backgroundType?: BackgroundType
  backgroundColor?: string // HEXカラー
  
  // 入力ソース
  inputSource: 'saved_character' | 'text_upload' | 'image_upload' | 'manual'
  
  // 保存済みキャラクター
  savedCharacterId?: string
  
  // テキストアップロード
  uploadedText?: string
  
  // 画像アップロード
  uploadedImage?: string // base64
  
  // 手動入力・編集
  manualInput?: ManualCharacterInput
  
  // 編集モード（他の入力ソースからの編集時）
  isEditMode?: boolean
  originalInput?: ManualCharacterInput // 編集前の元データ
  
  // 出力設定
  targetModel: AIModel
  
  // 追加設定
  additionalTags?: string[]
  negativeTags?: string[]
  stylePreset?: string
  seed?: number
  useProMode?: boolean
  useGemini3FlashMode?: boolean // Gemini 3 Flashモード（試験版）
  creativityLevel?: number // 1-10
}

// 生成されたプロンプト結果
export interface ImagePromptResult {
  positivePrompt: string
  negativePrompt: string

  // 日本語翻訳（わかりやすい説明）
  translatedPositivePrompt?: string
  translatedNegativePrompt?: string

  // モデル固有パラメータ
  modelSpecificParams?: {
    // Midjourney用
    aspectRatio?: string     // --ar 2:3
    stylize?: number         // --stylize 750
    chaos?: number           // --chaos 0
    version?: string         // --v 7
    draft?: boolean          // --draft

    // SD系共通
    cfgScale?: number
    steps?: number
    sampler?: string
    clipSkip?: number
  }

  // 解析された要素（デバッグ用）
  parsedElements?: {
    character?: string
    style?: string
    composition?: string
    environment?: string
    quality?: string[]
  }

  // メタデータ
  metadata?: {
    model: AIModel
    timestamp: string
    seed?: number
    proMode?: boolean
  }
}

// 保存済みプロンプト
export interface SavedImagePrompt {
  id: string
  title: string
  input: ImagePromptInput
  result: ImagePromptResult
  createdAt: string
  updatedAt: string
  memo?: string
  tags?: string[]
}

// nanobanana pro 画像生成用の型定義

// アスペクト比
export type ImageAspectRatio = '9:16' | '16:9' | '1:1' | '4:3' | '3:4'

// 画像サイズ
export type ImageSize = '1K' | '2K' | '4K'

// プロンプト入力方法
export type PromptInputMethod = 'generator' | 'saved' | 'direct'

// 生成モード
export type ImageGenerateMode = 'text_to_image' | 'image_edit'

// 参照画像の最大数
export const MAX_REFERENCE_IMAGES = 14

// 画像生成入力
export interface ImageGenerateInput {
  // プロンプト（ポジティブ）
  prompt: string
  // ネガティブプロンプト（オプション）
  negativePrompt?: string
  // 生成モード
  mode: ImageGenerateMode
  // 参考画像（image_editモード時、base64形式、最大14枚）
  referenceImages?: string[]
  // マスク画像（image_editモード時、base64形式）- 白い部分が編集対象
  maskImage?: string
  // 挿入画像（マスク部分に合成する画像、base64形式）
  insertImage?: string
  // アスペクト比
  aspectRatio: ImageAspectRatio
  // 画像サイズ
  imageSize: ImageSize
}

// 画像生成結果
export interface ImageGenerateResult {
  // 生成された画像（base64形式）
  imageData: string
  // MIMEタイプ
  mimeType: string
  // テキストレスポンス（あれば）
  textResponse?: string
  // メタデータ
  metadata: {
    model: string
    aspectRatio: ImageAspectRatio
    imageSize: ImageSize
    mode: ImageGenerateMode
    timestamp: string
  }
}